import * as Amplitude from '@amplitude/analytics-react-native';

import config from '@config';
import {
  CommentParentEntityEnum,
  TariffPlanCodeEnum,
  TutorialListTypeEnum,
  CourseListTypeEnum,
} from '@constants';
import { screens, ScreensNameType } from '../navigation/screens';
import BrazeService from './BrazeService';
import TagManagerService, {
  ITrackPurchaseParams,
} from './TagManagerService';

// 'subscription purchase' event is triggering on backend, keep it there to have all event's names in one place
export enum AnalyticEventNameEnum {
  PageView = 'page view',
  ContentPlay = 'content play',
  ContentWatch = 'content watch',
  ContentInterest = 'content interest',
  ContentStart = 'content start',
  SubscriptionPurchase = 'subscription purchase',
  SubscriptionCancel = 'subscription cancel',
  RecommendationClick = 'recommendation click',
  HubClick = 'hub click',
  HomeClick = 'home click',
  ContentSave = 'content save',
  ContentLike = 'content like',
  ContentShare = 'content share',
  NotificationClick = 'notification click',
  PostAdd = 'post add',
  PostComment = 'post comment',
  ChatMessage = 'chat message',
  SubmitFeedback = 'submit feedback',
  TagClick = 'tag click',
  GlobalSearch = 'global search',
  FormSubmitted = 'form submitted',
  CourseStarted = 'course started',
  CourseCompleted = 'course completed',
  CourseLessonStarted = 'course lesson started',
  CourseLessonCompleted = 'course lesson completed',
  CourseProgressUpdated = 'course progress updated',
}

export enum AnalyticFormSubmittedTypeEnum {
  ContactUs = 'Contact Us - App',
}

export enum AnalyticGlobalSearchEventTypeEnum {
  Static = 'static',
  Dynamic = 'dynamic',
}

export interface IAnalyticUserParams {
  email?: string;
  signUpDate?: Date;
  lastSeenDate?: Date;
}

export interface IAnalyticPageViewParams {
  pageName: string;
  subPageName?: string;
}

interface ITrackContentBaseParams {
  contentId: number;
  contentTitle: string;
  tagIds: number[];
  tagNames: string[];
  courseId?: number;
}

interface ITrackContentPlayParams extends ITrackContentBaseParams {
  categoryName?: string;
}

interface ITrackHomeContentParams {
  contentTitle: string;
  courseId?: number;
}

interface ITrackCourseBaseParams {
  courseId: number;
  courseTitle: string;
}

interface ITrackCourseLessonBaseParams {
  courseId: number;
  lessonId: number;
  lessonTitle: string;
}

interface ITrackCourseProgressUpdatedParams
  extends ITrackCourseBaseParams {
  value: number;
}

const TUTORIAL_SCREENS_BY_LIST_TYPE: Record<
  TutorialListTypeEnum,
  string
> = {
  [TutorialListTypeEnum.Recommended]: 'RecommendedTutorials',
  [TutorialListTypeEnum.Popular]: 'PopularTutorials',
  [TutorialListTypeEnum.Latest]: 'LatestTutorials',
  [TutorialListTypeEnum.History]: 'HistoryTutorials',
  [TutorialListTypeEnum.Saved]: 'SavedVideos',
  [TutorialListTypeEnum.Tag]: 'TaggedTutorials',
} as const;

const COURSE_SCREENS_BY_LIST_TYPE: Record<
  CourseListTypeEnum,
  string
> = {
  [CourseListTypeEnum.Recommended]: 'RecommendedCourses',
} as const;

/**
 * Gt the former screen name for analytics after renaming
 * the screen name in the app to keep measurements consistent
 */
export function getFormerScreenNameForAnalytics(route: {
  key: string;
  name: string;
  params?: any;
}) {
  switch (route.name) {
    case screens.Tutorials:
      const tutorialType = route.params?.type;

      if (tutorialType) {
        return TUTORIAL_SCREENS_BY_LIST_TYPE[
          tutorialType as TutorialListTypeEnum
        ];
      }

      return route.name;
    case screens.Courses:
      const courseType = route.params?.type;

      if (courseType) {
        return COURSE_SCREENS_BY_LIST_TYPE[
          courseType as CourseListTypeEnum
        ];
      }

      return route.name;
    default:
      return route.name;
  }
}

const parentScreenNameByScreen: Partial<
  Record<ScreensNameType, ScreensNameType>
> = {
  [screens.News]: screens.HomeTabNavigator,
  [screens.Latest]: screens.HomeTabNavigator,
  [screens.MyHub]: screens.HomeTabNavigator,
  [screens.UserNews]: screens.UserProfileTabNavigator,
  [screens.UserTracks]: screens.UserProfileTabNavigator,
  [screens.SearchTutorial]: screens.SearchTabNavigator,
  [screens.SearchProfile]: screens.SearchTabNavigator,
};

const subscriptionPlanNameByCode: Record<TariffPlanCodeEnum, string> =
  {
    [TariffPlanCodeEnum.Trial]: 'TRIAL',
    [TariffPlanCodeEnum.Monthly]: 'MONTHLY',
    [TariffPlanCodeEnum.Quarterly]: 'QUATERLY',
    [TariffPlanCodeEnum.Yearly]: 'YEARLY',
  };

class AnalyticsService {
  User: Amplitude.Identify | null = null;

  init() {
    Amplitude.init(config.AMPLITUDE_KEY, undefined, {
      minIdLength: 1,
    });
  }

  getSessionId() {
    return Amplitude.getSessionId();
  }

  /**
   * This will ensure that the user ID from the database instead of the device ID
   * will be used to identify user in analytics
   */
  setUserId(id: number) {
    try {
      Amplitude.setUserId(id?.toString());
      BrazeService.setUserId(id?.toString());
    } catch (error) {
      // Do nothing
    }
  }

  setUserParams(params: Record<string, any>) {
    try {
      this.User = new Amplitude.Identify();

      Object.keys(params).forEach((key) => {
        if (__DEV__) {
          const analytic = `Analytics setUserParam:${key}, val:${params[key]}`;
          return console.log(analytic);
        }

        this.User?.set(key, params[key]);
      });

      Amplitude.identify(this.User);
    } catch (error) {
      // Do nothing
    }
  }

  trackEvent(name: string, params?: Record<string, any>) {
    try {
      if (__DEV__) {
        const evParams = JSON.stringify(params);
        const analytic = `Analytics trackEvent:${name}, params:${evParams}`;
        return console.log(analytic);
      }

      Amplitude.track(name, params);
    } catch (error) {
      // Do nothing
    }
  }

  clear() {
    try {
      Amplitude.reset();
    } catch (error) {
      // Do nothing
    }
  }

  trackUserParams(params: IAnalyticUserParams) {
    this.setUserParams(params);
    BrazeService.trackUserParams(params);
  }

  trackScreenView(name: ScreensNameType) {
    const params: IAnalyticPageViewParams = {
      pageName: parentScreenNameByScreen[name] || name,
    };

    if (parentScreenNameByScreen[name]) {
      params.subPageName = name;
    }

    this.trackEvent(AnalyticEventNameEnum.PageView, params);
    BrazeService.trackEvent(AnalyticEventNameEnum.PageView, params);
  }

  trackContentWatch(params: ITrackContentBaseParams) {
    this.trackEvent(AnalyticEventNameEnum.ContentWatch, params);
    BrazeService.trackEvent(
      AnalyticEventNameEnum.ContentWatch,
      params,
    );
  }

  trackContentInterest(params: ITrackContentBaseParams) {
    this.trackEvent(AnalyticEventNameEnum.ContentInterest, params);
    BrazeService.trackEvent(
      AnalyticEventNameEnum.ContentInterest,
      params,
    );
  }

  trackContentStart(params: ITrackContentBaseParams) {
    this.trackEvent(AnalyticEventNameEnum.ContentStart, params);
    BrazeService.trackEvent(
      AnalyticEventNameEnum.ContentStart,
      params,
    );
  }

  trackContentPlay(params: ITrackContentPlayParams) {
    this.trackEvent(AnalyticEventNameEnum.ContentPlay, params);
    BrazeService.trackEvent(
      AnalyticEventNameEnum.ContentPlay,
      params,
    );
  }

  trackSubscriptionCancel(code: TariffPlanCodeEnum) {
    this.trackEvent(AnalyticEventNameEnum.SubscriptionCancel, {
      subscriptionPlan: subscriptionPlanNameByCode[code],
    });
    BrazeService.trackEvent(
      AnalyticEventNameEnum.SubscriptionCancel,
      {
        subscriptionPlan: subscriptionPlanNameByCode[code],
      },
    );
  }

  trackRecommendationContent(contentTitle: string) {
    this.trackEvent(AnalyticEventNameEnum.RecommendationClick, {
      contentTitle,
    });
    BrazeService.trackEvent(
      AnalyticEventNameEnum.RecommendationClick,
      {
        contentTitle,
      },
    );
  }

  trackHubContent(contentTitle: string) {
    this.trackEvent(AnalyticEventNameEnum.HubClick, {
      contentTitle,
    });
    BrazeService.trackEvent(AnalyticEventNameEnum.HubClick, {
      contentTitle,
    });
  }

  trackHomeContent(params: ITrackHomeContentParams) {
    this.trackEvent(AnalyticEventNameEnum.HomeClick, params);
    BrazeService.trackEvent(AnalyticEventNameEnum.HomeClick, params);
  }

  trackContentSave(params: ITrackContentBaseParams) {
    this.trackEvent(AnalyticEventNameEnum.ContentSave, params);
    BrazeService.trackEvent(
      AnalyticEventNameEnum.ContentSave,
      params,
    );
  }

  trackContentLike(params: ITrackContentBaseParams) {
    this.trackEvent(AnalyticEventNameEnum.ContentLike, params);
    BrazeService.trackEvent(
      AnalyticEventNameEnum.ContentLike,
      params,
    );
  }

  trackContentShare(params: ITrackContentBaseParams) {
    this.trackEvent(AnalyticEventNameEnum.ContentShare, params);
    BrazeService.trackEvent(
      AnalyticEventNameEnum.ContentShare,
      params,
    );
  }

  trackNotification(contentTitle: string) {
    this.trackEvent(AnalyticEventNameEnum.NotificationClick, {
      contentTitle,
    });
    BrazeService.trackEvent(AnalyticEventNameEnum.NotificationClick, {
      contentTitle,
    });
  }

  trackPostAdd() {
    this.trackEvent(AnalyticEventNameEnum.PostAdd);
    BrazeService.trackEvent(AnalyticEventNameEnum.PostAdd);
  }

  trackPostComment(commentLocation: CommentParentEntityEnum) {
    this.trackEvent(AnalyticEventNameEnum.PostComment, {
      commentLocation,
    });
    BrazeService.trackEvent(AnalyticEventNameEnum.PostComment, {
      commentLocation,
    });
  }

  trackChatMessage() {
    this.trackEvent(AnalyticEventNameEnum.ChatMessage);
    BrazeService.trackEvent(AnalyticEventNameEnum.ChatMessage);
  }

  trackSubmitFeedback() {
    this.trackEvent(AnalyticEventNameEnum.SubmitFeedback);
    BrazeService.trackEvent(AnalyticEventNameEnum.SubmitFeedback);
  }

  trackTagClick(name: string) {
    this.trackEvent(AnalyticEventNameEnum.TagClick, {
      tagName: name,
    });
    BrazeService.trackEvent(AnalyticEventNameEnum.TagClick, {
      tagName: name,
    });
  }

  trackGlobalSearch(
    query: string,
    type: AnalyticGlobalSearchEventTypeEnum,
  ) {
    this.trackEvent(AnalyticEventNameEnum.GlobalSearch, {
      query,
      type,
    });
  }

  trackUserContactUs(enquiryTypeId: number, content: string) {
    this.trackEvent(AnalyticEventNameEnum.FormSubmitted, {
      name: AnalyticFormSubmittedTypeEnum.ContactUs,
      enquiryTypeId,
      content,
    });
    BrazeService.trackEvent(AnalyticEventNameEnum.FormSubmitted, {
      name: AnalyticFormSubmittedTypeEnum.ContactUs,
      enquiryTypeId,
      content,
    });
  }

  trackCourseStarted(params: ITrackCourseBaseParams) {
    this.trackEvent(AnalyticEventNameEnum.CourseStarted, params);
    BrazeService.trackEvent(
      AnalyticEventNameEnum.CourseStarted,
      params,
    );
  }

  trackCourseCompleted(params: ITrackCourseBaseParams) {
    this.trackEvent(AnalyticEventNameEnum.CourseCompleted, params);
    BrazeService.trackEvent(
      AnalyticEventNameEnum.CourseCompleted,
      params,
    );
  }

  trackCourseLessonStarted(params: ITrackCourseLessonBaseParams) {
    this.trackEvent(
      AnalyticEventNameEnum.CourseLessonStarted,
      params,
    );
    BrazeService.trackEvent(
      AnalyticEventNameEnum.CourseLessonStarted,
      params,
    );
  }

  trackCourseLessonCompleted(params: ITrackCourseLessonBaseParams) {
    this.trackEvent(
      AnalyticEventNameEnum.CourseLessonCompleted,
      params,
    );
    BrazeService.trackEvent(
      AnalyticEventNameEnum.CourseLessonCompleted,
      params,
    );
  }

  trackCourseProgressUpdated(
    params: ITrackCourseProgressUpdatedParams,
  ) {
    this.trackEvent(
      AnalyticEventNameEnum.CourseProgressUpdated,
      params,
    );
    BrazeService.trackEvent(
      AnalyticEventNameEnum.CourseProgressUpdated,
      params,
    );
  }

  // TagManagerService events
  trackPurchase(params: ITrackPurchaseParams) {
    TagManagerService.trackPurchase(params);
  }
}

export default new AnalyticsService();
